<template>
  <div class="barcode-scanner-wrapper p-3">
    <!-- USB ready indicator -->
    <div class="usb-indicator" :class="{ 'usb-flash': flashActive }">
      <span class="usb-dot"></span>
      ماسح USB جاهز
      <span v-if="lastScanned" class="ms-2 text-success fw-bold">{{ lastScanned }}</span>
    </div>

    <!-- Camera toggle -->
    <button
      class="btn btn-sm mt-2"
      :class="cameraScanning ? 'btn-danger' : 'btn-outline-secondary'"
      @click="toggleCamera"
    >
      <i class="pi" :class="cameraScanning ? 'pi-times' : 'pi-camera'"></i>
      {{ cameraScanning ? 'إغلاق الكاميرا' : 'تشغيل الكاميرا' }}
    </button>

    <div v-if="cameraScanning" class="camera-viewport mt-2">
      <div :id="scannerDivId"></div>
    </div>

    <p v-if="errorMsg" class="text-danger mt-1 mb-0 small">{{ errorMsg }}</p>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode';

export default {
  name: 'BarcodeScanner',

  props: {
    mode: { type: String, required: true },
  },

  emits: ['scanned'],

  data() {
    return {
      cameraScanning: false,
      scanner: null,
      scannerDivId: `bc-reader-${Math.random().toString(36).substr(2, 9)}`,
      errorMsg: '',
      flashActive: false,
      lastScanned: '',

      keyBuffer:   [],
      lastKeyTime: 0,
      bufferTimer: null,

      lastScan: { value: null, time: 0 },
      DEBOUNCE_MS: 2000,
    };
  },

  methods: {
    // ── USB keyboard scanner ────────────────────────────────────────────────

    handleKeydown(e) {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return;
      if (document.activeElement?.isContentEditable) return;

      const now = Date.now();

      if (e.key === 'Enter') {
        clearTimeout(this.bufferTimer);
        const buffered = this.keyBuffer.splice(0);
        if (buffered.length) this.processScanned(buffered.join(''));
        return;
      }

      // Reset buffer if gap is too long (human typing, not scanner)
      if (now - this.lastKeyTime > 150 && this.keyBuffer.length) {
        this.keyBuffer = [];
        clearTimeout(this.bufferTimer);
      }

      if (e.key.length === 1) {
        this.keyBuffer.push(e.key);
        this.lastKeyTime = now;

        clearTimeout(this.bufferTimer);
        this.bufferTimer = setTimeout(() => {
          const buffered = this.keyBuffer.splice(0);
          if (buffered.length) this.processScanned(buffered.join(''));
        }, 200);
      }
    },

    // ── Parse & emit ────────────────────────────────────────────────────────

    processScanned(raw) {
      const match = raw.trim().match(/^STU-(\d+)$/i);
      if (!match) return;

      const studentId = match[1];
      const now = Date.now();
      if (studentId === this.lastScan.value && now - this.lastScan.time < this.DEBOUNCE_MS) return;

      this.lastScan      = { value: studentId, time: now };
      this.lastScanned   = `STU-${studentId}`;
      this.flashActive   = true;
      setTimeout(() => { this.flashActive = false; }, 600);

      this.$emit('scanned', { studentId, mode: this.mode });
    },

    // ── Camera ──────────────────────────────────────────────────────────────

    async toggleCamera() {
      this.cameraScanning ? await this.stopCamera() : await this.startCamera();
    },

    async startCamera() {
      this.errorMsg      = '';
      this.cameraScanning = true;
      await this.$nextTick();

      try {
        this.scanner = new Html5Qrcode(this.scannerDivId);
        await this.scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 350, height: 100 } },
          (decoded) => this.processScanned(decoded),
          () => {}
        );
      } catch {
        this.errorMsg      = 'تعذّر الوصول إلى الكاميرا. يرجى السماح بالإذن وإعادة المحاولة.';
        this.cameraScanning = false;
      }
    },

    async stopCamera() {
      if (this.scanner?.isScanning) {
        try { await this.scanner.stop(); } catch {}
      }
      this.scanner       = null;
      this.cameraScanning = false;
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.bufferTimer);
    this.stopCamera();
  },
};
</script>

<style scoped>
.barcode-scanner-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
}

.usb-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #15803d;
  transition: background 0.3s;
}

.usb-indicator.usb-flash {
  background: #dcfce7;
  border-color: #22c55e;
}

.usb-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.camera-viewport {
  border: 2px solid #0d6efd;
  border-radius: 8px;
  overflow: hidden;
  max-width: 400px;
}
</style>
